import { Play, Download, Trash2, ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { ImageFile } from '../types';

interface ImageListItemCompactProps {
  image: ImageFile;
  onProcessImage: (image: ImageFile) => void;
  onDownloadImage: (image: ImageFile) => void;
  onRemoveImage: (id: string) => void;
  isProcessing: boolean;
  isDarkMode: boolean;
}

export default function ImageListItemCompact({
  image,
  onProcessImage,
  onDownloadImage,
  onRemoveImage,
  isProcessing,
  isDarkMode
}: ImageListItemCompactProps) {
  // Configurações removidas - nenhuma state necessária

  const getStatusIcon = (status: ImageFile['status']) => {
    switch (status) {
      case 'pending':
        return <ImageIcon className="w-4 h-4 text-blue-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Tamanho formatado
  const fileSize = image.file ? formatFileSize(image.file.size) : 'Tamanho desconhecido';

  // Formato atual (atualiza após conversão)
  const originalExt = image.file ? (image.file.type.split('/')[1]?.toUpperCase() || image.file.name.split('.').pop()?.toUpperCase()) : 'N/A';
  const processedExt = image.resizedBlob?.type ? image.resizedBlob.type.split('/')[1]?.toUpperCase() : undefined;
  const selectedExt = image.individualSettings?.format && image.individualSettings.format !== 'original'
    ? image.individualSettings.format.toUpperCase()
    : undefined;
  const currentFormat = image.status === 'completed' ? (processedExt || selectedExt || originalExt) : originalExt;

  // Dimensões exibidas
  const displayedDimensions = image.status === 'completed' && image.targetSize
    ? `${image.targetSize.width}×${image.targetSize.height}`
    : `${image.originalSize.width}×${image.originalSize.height}`;

  // Verificação defensiva para evitar erros quando image.file é null/undefined
  if (!image.file) {
    return (
      <div className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
        isDarkMode 
          ? 'bg-[#2A2E3F] border-[#4A4F64]' 
          : 'bg-white border-[#E5E5E5]'
      }`}>
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
          <div className="min-w-0">
            <div className={`font-medium ${isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'}`}>
              Arquivo não disponível
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
              Erro ao carregar arquivo
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={() => onRemoveImage(image.id)} className={`p-2 rounded-lg transition-all duration-300 ${isDarkMode ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-100 text-red-600'}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
          isDarkMode 
            ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52]' 
            : 'bg-white border-[#E5E5E5] hover:bg-[#F8F9FA]'
        }`}
      >
        {/* Info da Imagem */}
        <div className="flex items-center space-x-4 flex-grow min-w-0 pr-4">
          {/* Preview */}
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
            {image.preview ? (
              <img src={image.preview} alt={image.file?.name || 'Imagem'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>

          {/* Detalhes */}
          <div className="min-w-0">
            <div className={`font-medium truncate hidden lg:block ${isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'}`}>
              {image.file?.name || 'Nome não disponível'}
            </div>
            
            {/* Mobile/Tablet - Layout separado */}
            <div className="lg:hidden">
              <div className={`text-sm ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
                {fileSize}
              </div>
              {image.originalSize && image.originalSize.width > 0 && (
                <div className={`text-sm ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
                  {displayedDimensions}
                </div>
              )}
              <div className={`text-sm uppercase ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
                {currentFormat}
              </div>
            </div>

            {/* Desktop - Layout em linha única otimizado */}
            <div className={`hidden lg:block text-sm ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
              {image.originalSize && image.originalSize.width > 0 && (
                <span>{displayedDimensions}</span>
              )}
              {image.originalSize && image.originalSize.width > 0 && <span> • </span>}
              <span>{fileSize}</span>
              <span> • </span>
              <span className="uppercase">{currentFormat}</span>
            </div>

            {/* Status - Mobile & Tablet */}
            <div className="flex items-center space-x-1.5 mt-1 lg:hidden">
              {getStatusIcon(image.status)}
              <span className={`text-xs ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}> 
                {image.status === 'pending' && 'Pendente'}
                {image.status === 'processing' && 'Processando...'}
                {image.status === 'completed' && 'Concluído'}
                {image.status === 'error' && 'Erro'}
              </span>
            </div>

            {/* Status - Desktop */}
            <div className="hidden lg:flex items-center space-x-2 mt-1 flex-shrink-0">
              {getStatusIcon(image.status)}
              <span className={`text-sm ${isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'}`}>
                {image.status === 'pending' && 'Pendente'}
                {image.status === 'processing' && 'Processando...'}
                {image.status === 'completed' && 'Concluído'}
                {image.status === 'error' && 'Erro'}
              </span>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1 sm:gap-2 ml-auto">
          {image.status === 'pending' && !isProcessing && (
            <button onClick={() => onProcessImage(image)} className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-all duration-300 md:w-auto w-24">
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline text-center">Converter</span>
              <span className="sm:hidden text-center">Converter</span>
            </button>
          )}

          {image.status === 'completed' && (
            <button onClick={() => onDownloadImage(image)} className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-all duration-300">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline text-center">Baixar</span>
              <span className="sm:hidden text-center">Baixar</span>
            </button>
          )}

          <button 
            onClick={() => onRemoveImage(image.id)} 
            className={`p-2 rounded-lg transition-all duration-300 flex items-center justify-center w-8 h-8 ${
              isDarkMode 
                ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' 
                : 'bg-red-100 hover:bg-red-200 text-red-600'
            }`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
} 